import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React, { useEffect, useRef, useState } from 'react';
import { getSeoVisibility } from '../../../src/appconfig';
import NewFooter from '../../../src/components/HomePage/NewFooter';
import NewHeader from '../../../src/components/HomePage/NewHeader';
import { getKeyAndIdForStage } from '../../../src/components/Parenting/common';
import ParentingGeneralItems from '../../../src/components/Parenting/ParentingGeneralItems';
import InstallNudge from '../../../src/components/QnaPage/InstallNudge/InstallNudge';
import { tabByKeyFetcher } from '../../../src/datafetchers/CategoryPage';
import { getFeedsData } from '../../../src/datafetchers/HomePage';
import { getGrowthData } from '../../../src/datafetchers/PregnancyPage';
import GlobalStyle from '../../../src/styles/globalStylesV2';
import {
  getDeviceType,
  getFilteredCategoryContent,
} from '../../../src/Utils/helper';
import { googlePlayStoreLink } from '../../../src/Constants/branchUrls';
import { storeDataFetcher } from '../../../src/datafetchers/Store';
import { fireViewedGeneralScreenEvent } from '../../../src/Utils/useFirebase';

export async function getStaticPaths() {
  return {
    paths: [
      { params: { parentingPage: 'infant' } },
      { params: { parentingPage: 'pregnancy-planning' } },
      { params: { parentingPage: 'pregnancy' } },
      { params: { parentingPage: 'toddler' } },
    ],
    fallback: 'blocking',
  };
}

export async function getStaticProps(context) {
  let type = context.params.parentingPage;
  let { key, id, stage, metaTitle, metaDescription } =
    getKeyAndIdForStage(type);
  const params = {
    skip: 0,
    limit: 20,
    key: key,
    isForGeneralTabFromWeb: true,
  };
  const generalItemData = await tabByKeyFetcher(params);
  const feedParams = {
    filter: 'post',
    page: 1,
    tag: id,
  };
  const questionData = await getFeedsData(feedParams);
  const recentlyAskedParams = {
    tag: id,
    filter: 'post',
    page: 1,
    showLatestWebPosts: true,
  };
  const recentlyAskedQues = await getFeedsData(recentlyAskedParams);
  const babyGrowthData = stage == 'pregnant' ? await getGrowthData(18) : '';
  const data = await storeDataFetcher({
    skip: 0,
    limit: 10,
    lang: 'en',
    inApp: false,
    desktopView: true,
    key: 'store',
  });
  return {
    props: {
      generalItemData: generalItemData,
      questionData: questionData?.data?.posts,
      recentlyAskedQues: recentlyAskedQues?.data?.posts?.items?.slice(0, 3),
      babyGrowthData:
        stage == 'pregnant' ? babyGrowthData?.baby_data : babyGrowthData,
      stage,
      tagId: id,
      metaTitle,
      metaDescription,
      type,
      categoryData: getFilteredCategoryContent(data?.data?.categories),
    },
    revalidate: 60,
  };
}

export default function ParentingStagePages(props) {
  const {
    generalItemData,
    questionData,
    recentlyAskedQues,
    babyGrowthData,
    stage,
    metaTitle,
    metaDescription,
    tagId,
    type,
    categoryData,
  } = props;
  const installLink = googlePlayStoreLink;
  const [showInstallStrip, setShowInstallStrip] = useState(true);
  const deviceType = useRef();
  const openGraphData = {
    title: metaTitle,
    description: metaDescription,
    type: 'website',
    url: `https://mylofamily.com/parenting/${type}`,
    images: [
      {
        url: 'https://mylofamily.com/v2/static/images/512mylo.png',
        width: 512,
        height: 512,
      },
    ],
  };
  useEffect(() => {
    fireViewedGeneralScreenEvent();
    deviceType.current = getDeviceType();
  }, []);

  const closeInstallStrip = () => {
    fireEvent({
      category: 'discard_strip',
      screen_name: getScreenName(),
      page_url: typeof window != 'undefined' ? window.location.href : 'NA',
      parent: 'Install App Nudge Strip Mobile',
    });
    setShowInstallStrip(false);
  };
  const topImage = generalItemData?.data?.items?.[0]?.mobileWebImage;
  const Footnotes = generalItemData?.data?.items?.filter(function (item) {
    return item?.itemType === 'SEO_FOOTNOTE';
  });
  const { heading, htmlText } =
    Footnotes && Footnotes.length > 0 ? Footnotes?.[0] : '';
  return (
    <>
      <Head>
        {topImage && <link rel="preload" as="image" href={topImage} />}
      </Head>
      {getSeoVisibility() ? (
        <>
          <NextSeo
            title={metaTitle}
            description={metaDescription}
            openGraph={openGraphData}
          />
        </>
      ) : null}
      <GlobalStyle />
      <div>
        <NewHeader
          categoryData={categoryData}
          showCart={true}
          showTrackOrder={true}
          nostrip={true}
        />

        {showInstallStrip ? (
          <InstallNudge
            installLink={installLink}
            closeInstallStrip={closeInstallStrip}
            appIsPreasent={false}
          />
        ) : null}
        <ParentingGeneralItems
          parentingPageData={generalItemData}
          isStagePage={true}
          recentlyAskedQues={recentlyAskedQues}
          questionData={questionData}
          stage={stage}
          tagId={tagId}
          babyGrowthData={babyGrowthData}
        />
      </div>
      <NewFooter heading={heading} content={htmlText} />
    </>
  );
}
